import * as React from 'react';
import { useRecoilValue } from 'recoil';

import { useSurvey } from '@src/hooks/useSurvey';
import { userAtom } from '@src/recoil/atoms/user';
import { trimCharacters } from '@src/utils/strings';
import { SurveyQuestionProps } from './SurveyQuestion';

export interface TypeEmailProps extends SurveyQuestionProps {}

/**
 * @name TypeEmail
 * @description A form input for collecting our users email. This component
 * makes use of the browsers built in "email" validation rules.
 */
export const TypeEmail = (props: TypeEmailProps) => {
  const { question } = props;

  // Hooks
  const ref = React.useRef<HTMLInputElement>(null);
  const { answers, setAnswer } = useSurvey();
  const value = answers[question.name];
  const { email } = useRecoilValue(userAtom);

  // Setup
  const { properties = {} } = question;
  const { autocomplete } = properties;

  // Detect login credentials
  React.useEffect(() => {
    if (!ref.current || !email) return;

    ref.current.value = email;
    ref.current.disabled = true;

    setAnswer(question.name, email);
  }, [email, question.name]);

  // Handlers
  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.currentTarget.setCustomValidity('');

    const value = event.target.value !== '' ? event.target.value : undefined;
    const answer = value ? trimCharacters(value, question.name) : value;

    setAnswer(question.name, answer);
  };

  return (
    <input
      ref={ref}
      autoComplete={autocomplete}
      className="m-0 rounded-none border-0 p-3 font-beatrice-light text-14 md:py-4 md:px-8 md:text-18"
      defaultValue={value}
      name={question.name}
      onChange={onChangeHandler}
      placeholder={question.placeholder ?? ''}
      required={!!question.required}
      type="email"
    />
  );
};
